/**
 * Armed-tool tray: a beige raised panel fixed to the bottom center, holding
 * 52px square tool slots. One tool is always armed; non-hand tools auto-revert
 * to the hand after 20 s of no re-arming. Thumb-first: big targets, no text
 * selection, safe-area padded.
 */
import { injectBaseStyles, pixelIcon } from './win94';

export type Tool = 'hand' | 'apple' | 'sponge' | 'ball';

const AUTO_REVERT_MS = 20_000;
const SLIDE_MS = 220;

export class Tray {
  private readonly el: HTMLDivElement;
  private readonly onArm: (tool: Tool) => void;
  private readonly slots = new Map<Tool, HTMLButtonElement>();
  private readonly used = new Set<Tool>();
  private armedTool: Tool = 'hand';
  private revertTimer = 0;

  constructor(parent: HTMLElement, onArm: (tool: Tool) => void) {
    injectBaseStyles();
    this.onArm = onArm;
    this.el = document.createElement('div');
    this.el.className = 'w94-raised w94-font';
    this.el.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:0',
      'transform:translateX(-50%)',
      'display:flex',
      'align-items:center',
      'gap:6px',
      'padding:4px 6px calc(4px + env(safe-area-inset-bottom))',
      'box-sizing:border-box',
      'pointer-events:auto',
      'touch-action:manipulation',
      'user-select:none',
      '-webkit-user-select:none',
      'z-index:20',
    ].join(';');
    parent.appendChild(this.el);
    this.addSlot('hand');
    this.applyArmedLook();
  }

  get armed(): Tool {
    return this.armedTool;
  }

  /** Adds the slot (if absent), sliding it in; pulses until first markUsed. */
  reveal(tool: Tool): void {
    if (this.slots.has(tool)) return;
    const btn = this.addSlot(tool);
    btn.style.opacity = '0';
    btn.style.transform = 'translateY(16px)';
    void btn.offsetWidth; // commit start state before transitioning
    btn.style.transition = `transform ${SLIDE_MS}ms ease-out, opacity ${SLIDE_MS}ms ease-out`;
    btn.style.opacity = '1';
    btn.style.transform = 'translateY(0)';
    window.setTimeout(() => {
      btn.style.transition = '';
      btn.style.opacity = '';
      btn.style.transform = '';
      // pulse after the slide so its transform animation doesn't fight it
      if (!this.used.has(tool)) btn.classList.add('w94-pulse');
    }, SLIDE_MS + 40);
  }

  /** Stops the attract pulse permanently for this tool. */
  markUsed(tool: Tool): void {
    this.used.add(tool);
    this.slots.get(tool)?.classList.remove('w94-pulse');
  }

  /** Arms a tool, restarts the auto-revert timer, and fires onArm. */
  arm(tool: Tool): void {
    this.armedTool = tool;
    this.applyArmedLook();
    this.resetRevertTimer();
    this.onArm(tool);
  }

  private onTap(tool: Tool): void {
    // tapping the already-armed non-hand tool reverts to the hand
    this.arm(tool === this.armedTool && tool !== 'hand' ? 'hand' : tool);
  }

  private addSlot(tool: Tool): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'w94-btn';
    btn.setAttribute('aria-label', `${tool} tool`);
    btn.style.cssText = [
      'width:52px',
      'height:52px',
      'flex:0 0 auto',
      'pointer-events:auto',
    ].join(';');
    btn.appendChild(pixelIcon(tool));
    btn.addEventListener('click', () => this.onTap(tool));
    this.el.appendChild(btn);
    this.slots.set(tool, btn);
    return btn;
  }

  private applyArmedLook(): void {
    for (const [tool, btn] of this.slots) {
      btn.classList.toggle('w94-armed', tool === this.armedTool);
    }
  }

  private resetRevertTimer(): void {
    if (this.revertTimer !== 0) {
      window.clearTimeout(this.revertTimer);
      this.revertTimer = 0;
    }
    if (this.armedTool !== 'hand') {
      this.revertTimer = window.setTimeout(() => {
        this.revertTimer = 0;
        this.arm('hand');
      }, AUTO_REVERT_MS);
    }
  }
}
